import { TileTreeNodesProps } from 'business/models/CustomModels';
import React, { useEffect, useState, memo, useContext } from 'react';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { EPlateState } from 'enums/EPlatestate';
import ETreeTypeSortOrder from 'enums/ETreeTypeSortOrder';
import TreeContext from './TreeContext';
import TreeNodesContainer from './TreeNodesContainer';
import ETreeGroupTypes from 'enums/ETreeGroupTypes';
import { IStyle } from 'Styles';
import { TreeNodeDTO } from 'business/models/Models';
import { DraggableContainer } from 'components/DraggableContainer';
import { copyPasteValidation } from 'components/explorer/utils';
import { usePasteNodesHandler } from 'components/explorer/hooks';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import ETreeActionTypes from 'enums/ETreeActionTypes';
import useRMLite from 'CustomHooks/useRMLite';

function loadImageIcon(node) {
  let image;
  if (
    node.type === ETreeNodeTypes.ProjectsFolder ||
    node.type === ETreeNodeTypes.ProjectFolder
  ) {
    image = require(`components/main/images/TreeControl/Folder.png`);
    return image;
  }

  if (node.type === ETreeNodeTypes.Project) {
    image = require(`components/main/images/TreeControl/Project.png`);
    return image;
  }
  try {
    if (node.type === ETreeNodeTypes.Experiment) {
      if (
        node.extra.plateState === EPlateState.Designed ||
        node.extra.plateState === EPlateState.Dispensed
      ) {
        image = require(`components/main/images/${node.type}_${node.extra.state}.png`);
      } else {
        image = require(`components/main/images/${node.type}.png`);
      }
    } else {
      image = require(`components/main/images/${node.type}.png`);
    }
  } catch (e) {
    image = require('components/main/images/Projects.png');
    console.error(e);
  }
  return image;
}

const groupTreeData = (
  data: Array<any>,
  field: string,
): { data: Array<any>; type: string } => {
  return data.reduce(function (r, a) {
    const category =
      field === ETreeGroupTypes.Type
        ? a[field].includes('Folder')
          ? 'Folder'
          : a[field]
        : a[field];
    const key = field === ETreeGroupTypes.Type ? ETreeTypeSortOrder[category] : a[field];
    r[key] = r[key] || {
      data: [],
      category: category,
    };
    r[key]['data'].push(a);
    return r;
  }, Object.create(null));
};

const getSortedTreeNodes = (treeNodes, sortBy) => {
  return Array.isArray(treeNodes)
    ? treeNodes.sort((first, second) => {
        if (sortBy === 'type') {
          const firstType = first.type.toLowerCase();
          const secondType = second.type.toLowerCase();

          const isFirstFolder = firstType.includes('folder');
          const isSecondFolder = secondType.includes('folder');

          if (isFirstFolder && isSecondFolder) {
            return first.name.toLowerCase().localeCompare(second.name.toLowerCase());
          } else if (isFirstFolder) {
            return -1;
          } else if (isSecondFolder) {
            return 1;
          } else {
            return firstType.localeCompare(secondType);
          }
        } else {
          return (
            first[sortBy]
              ?.toLowerCase?.()
              .localeCompare?.(second[sortBy]?.toLowerCase?.()) ?? 0
          );
        }
      })
    : [];
};

function TileTreeNodes(props: TileTreeNodesProps) {
  const { node, nodeClickHandler, treeControlState, treeNodesFilterQuery } = props;
  const treeContext = useContext(TreeContext);
  const [nodes, setNodes] = useState([]);
  const pasteNodesHandler = usePasteNodesHandler();
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);
  const rmLite = useRMLite();

  useEffect(() => {
    if (node?.length) {
      let _nodes;
      let treeNodes = [...node];
      if (treeNodesFilterQuery) {
        treeNodes = treeNodes.filter((item: TreeNodeDTO) =>
          item.name.toLowerCase().includes(treeNodesFilterQuery.toLowerCase()),
        );
      }
      _nodes = getSortedTreeNodes(treeNodes, treeContext.sortBy);
      _nodes = Array.isArray(_nodes)
        ? groupTreeData(treeNodes, treeContext.groupBy)
        : treeNodes;
      setNodes(_nodes);
    } else {
      setNodes([]);
    }
  }, [node, treeContext.groupBy, treeContext.sortBy, treeNodesFilterQuery]);

  const postRefreshMessage = (sourceNode) => () => {
    explorerBroadcastChannel.postMessage({
      action: CHANNEL_ACTIONS.EXPLORER_ADD_NODE,
      payload: { parentId: treeControlState.selectedExplorerNode.id },
    });
    explorerBroadcastChannel.postMessage({
      action: CHANNEL_ACTIONS.EXPLORER_ADD_NODE,
      payload: { parentId: sourceNode.parentId },
    });
  };
  const handleDrop = (nodes: any[], operationType: ETreeActionTypes) => {
    if (nodes) {
      pasteNodesHandler(
        operationType,
        nodes,
        treeControlState.selectedExplorerNode,
        postRefreshMessage(nodes[0]),
        false,
      );
    }
  };

  const validateDropFile = (draggedNodes, currentNode) => {
    return copyPasteValidation(draggedNodes, currentNode).unableToPasteNodes.length <= 0;
  };

  const nodesComponent = (
    <div id="tile-view-nodes-top-container" className="h-100" style={styles.topContainer}>
      <div id="tile-view-nodes-inner-container" style={styles.innerContainer}>
        <div id="tile-view-nodes-nodes-container" style={styles.nodesContainer}>
          <TreeNodesContainer
            treeNodes={nodes}
            selectedNodes={treeControlState.selectedNodes.filter(
              (node) => node !== treeControlState.selectedExplorerNode,
            )}
            viewType={treeContext.viewType}
            groupBy={treeContext.groupBy}
            nodeClickHandler={nodeClickHandler}
            loadImageIcon={loadImageIcon}
            selectedExplorerNode={treeControlState.selectedExplorerNode}
          />
        </div>
      </div>
    </div>
  );

  return rmLite ? (
    nodesComponent
  ) : (
    <DraggableContainer
      enableDropOnly
      node={treeControlState.selectedExplorerNode}
      nodeName={treeControlState.selectedExplorerNode?.name}
      selectedFiles={[]}
      handleDrop={handleDrop}
      validateDrop={validateDropFile}
      className="h-100"
    >
      {nodesComponent}
    </DraggableContainer>
  );
}

const styles: IStyle = {
  topContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  innerContainer: {
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
  },
  nodesContainer: {
    marginTop: '10px',
    marginBottom: '10px',
    width: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
};

export default memo(TileTreeNodes);
